import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import SPAPill from '../../shared/SPAPill';
import { tintStyle } from '../../shared/tint';
import { lessonSectionsPath, unitEditPath, useContentMount } from '../contentMount';
import RemoveUnitDialog from './RemoveUnitDialog';
import { ProgramLesson, ProgramUnit } from './types';
import UnitActionsMenu from './UnitActionsMenu';

interface UnitCardProps {
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  isDragging: boolean;
  programId: number;
  unit: ProgramUnit;
}

const cardClass = cn(
  'tw-box-border tw-rounded-lg tw-border tw-border-solid tw-border-spa-border-secondary',
  'tw-bg-spa-bg-secondary tw-shadow-sm tw-overflow-hidden',
);

const gripClass = cn(
  'tw-flex tw-h-10 tw-w-7 tw-shrink-0 tw-items-center tw-justify-center tw-rounded',
  'tw-cursor-grab active:tw-cursor-grabbing tw-text-spa-tertiary tw-transition-colors',
  'hover:tw-bg-spa-bg-hover hover:tw-text-spa-primary',
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-spa-accent',
);

const thumbClass = cn(
  'tw-grid tw-h-16 tw-w-16 tw-shrink-0 tw-place-items-center tw-overflow-hidden tw-rounded-md',
  'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg-tertiary',
);

const lessonRowClass = cn(
  'tw-grid tw-grid-cols-[1.75rem_minmax(0,1fr)_auto] tw-items-baseline tw-gap-2 tw-py-1 tw-text-sm',
);

// spa-secondary, not tertiary: the eyebrow sits on the unit's color tint, where tertiary drops
// under 4.5:1 in both themes.
const eyebrowClass = cn(
  'tw-flex tw-flex-wrap tw-items-center tw-gap-2',
  'tw-text-[0.6875rem] tw-font-bold tw-uppercase tw-tracking-wider tw-text-spa-secondary',
);

const metaClass = 'tw-flex tw-flex-wrap tw-items-center tw-gap-x-3.5 tw-gap-y-1.5 tw-text-xs tw-text-spa-secondary';

const titleClass = 'tw-m-0 tw-mb-1.5 tw-mt-1 tw-text-lg tw-font-bold tw-leading-tight tw-text-spa-heading';

const lessonNumber = (lesson: ProgramLesson) => {
  if (lesson.display_type === 'icon' && lesson.icon_class) {
    return <span aria-hidden="true" className={cn(lesson.icon_class, 'tw-text-spa-accent-text')} />;
  }
  if (lesson.display_type === 'none' || lesson.kind === 'opening' || lesson.kind === 'closing') return '—';

  return lesson.title_number ?? '—';
};

const LessonRow = ({ lesson, programId }: { lesson: ProgramLesson; programId: number }) => {
  const others = lesson.program_ids.length - 1;

  return (
    <li className={lessonRowClass}>
      <span className="tw-text-right tw-text-xs tw-tabular-nums tw-text-spa-tertiary">{lessonNumber(lesson)}</span>
      <Link
        className="tw-text-spa-primary hover:tw-text-spa-accent-text-hover tw-no-underline"
        to={lessonSectionsPath(programId, lesson.id, useContentMount())}
      >
        {lesson.title}
      </Link>
      <span className="tw-whitespace-nowrap tw-text-xs tw-text-spa-tertiary">
        {others > 0 && `${lesson.program_ids.length} programs`}
      </span>
    </li>
  );
};

const Fact = ({ children, warn = false }: { children: React.ReactNode; warn?: boolean }) => (
  <span className={warn ? 'tw-text-spa-warning' : undefined}>{children}</span>
);

// One unit as the units page shows it: the facts a sysadmin checks before publishing, then the
// lessons as the table of contents numbers them. The header row is the tinted band, carrying the
// unit's color the way the TOC tints its unit headers, so it always covers exactly the header.
const UnitCard = ({ dragHandleProps, isDragging, programId, unit }: UnitCardProps) => {
  const [removeOpen, setRemoveOpen] = useState(false);
  const mount = useContentMount();
  const others = unit.programs_count - 1;

  return (
    <div className={cn(cardClass, isDragging && 'tw-shadow-xl tw-border-spa-accent')}>
      <div className="tw-flex tw-items-center tw-gap-3.5 tw-px-4 tw-py-3.5" style={tintStyle(unit.color)}>
        <div {...dragHandleProps} aria-label={`Reorder ${unit.title_with_position}`} className={gripClass}>
          <Icon icon="Drag" size={16} />
        </div>
        <div className={cn(thumbClass, !unit.student_text_image_thumb_url && 'tw-border-dashed')}>
          {unit.student_text_image_thumb_url ? (
            <img alt="" className="tw-h-full tw-w-full tw-object-cover" src={unit.student_text_image_thumb_url} />
          ) : (
            <span className="tw-text-lg tw-font-bold tw-text-spa-tertiary">{unit.display_number}</span>
          )}
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          <div className={eyebrowClass}>
            <span
              aria-hidden="true"
              className="tw-h-2.5 tw-w-2.5 tw-rounded-sm tw-border tw-border-solid tw-border-black/10"
              style={{ backgroundColor: `#${unit.color || '522e91'}` }}
            />
            Unit {unit.display_number}
            {unit.color && (
              <span className="tw-font-normal tw-normal-case tw-tracking-normal">#{unit.color}</span>
            )}
            {unit.call_out_text && (
              <span
                className="tw-rounded-full tw-px-2 tw-text-[0.65rem] tw-normal-case tw-tracking-normal tw-text-white"
                style={{ backgroundColor: `#${unit.call_out_color || 'd9482b'}` }}
              >
                {unit.call_out_text}
              </span>
            )}
          </div>
          <h3 className={titleClass}>
            <Link
              className="tw-text-inherit hover:tw-text-spa-accent-text-hover tw-no-underline"
              to={unitEditPath(programId, unit.id, mount)}
            >
              {unit.title}
            </Link>
          </h3>
          <div className={metaClass}>
            <SPAPill tone={unit.publish ? 'success' : 'warning'}>
              {unit.publish ? 'Published' : 'Unpublished'}
            </SPAPill>
            <Fact>{unit.lessons.length} {unit.lessons.length === 1 ? 'lesson' : 'lessons'}</Fact>
            <Fact>
              {others > 0
                ? `Shared with ${others} other ${others === 1 ? 'program' : 'programs'}`
                : 'Only in this program'}
            </Fact>
            <Fact warn={!unit.student_text_image_thumb_url}>
              {unit.student_text_image_thumb_url ? 'Student text image ✓' : 'No student text image'}
            </Fact>
            {unit.has_video && <Fact>Unit video ✓</Fact>}
          </div>
        </div>
        <div className="tw-shrink-0">
          <UnitActionsMenu programId={programId} unit={unit} onRemove={() => setRemoveOpen(true)} />
        </div>
      </div>
      <ol className="tw-m-0 tw-grid tw-list-none tw-gap-x-6 tw-px-4 tw-py-3 md:tw-grid-cols-2">
        {unit.lessons.length === 0 && (
          <li className="tw-py-1 tw-text-sm tw-italic tw-text-spa-tertiary md:tw-col-span-2">
            No lessons in this unit yet.
          </li>
        )}
        {unit.lessons.map(lesson => <LessonRow key={lesson.id} lesson={lesson} programId={programId} />)}
      </ol>

      <RemoveUnitDialog open={removeOpen} programId={programId} unit={unit} onOpenChange={setRemoveOpen} />
    </div>
  );
};

export default UnitCard;
